import { useEffect, useRef, useState } from 'react';

/** Seconds between two reloads while the refresh is late, so a stuck run is not hammered. */
const RETRY_SECONDS = 15;
/** When the next refresh is due: the API's slot (`refresh` is always in the answer). */
export const nextRefreshOf = (reply: { refresh: { nextRefreshAt: string } }): number =>
  Date.parse(reply.refresh.nextRefreshAt);

/**
 * Seconds left until `target` (a timestamp), ticking each second. At zero it calls `onDue` once,
 * and again every 15 s while the new answer is still late. `undefined` target: nothing runs.
 */
export function useCountdown(target: number | undefined, onDue: () => void): number | undefined {
  const [now, setNow] = useState(() => Date.now());
  const due = useRef(onDue);
  due.current = onDue;
  const lastCall = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (target === undefined || Number.isNaN(target)) return;
    lastCall.current = undefined;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      if (current < target) return;
      const last = lastCall.current;
      if (last === undefined || current - last >= RETRY_SECONDS * 1000) {
        lastCall.current = current;
        due.current();
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [target]);

  if (target === undefined || Number.isNaN(target)) return undefined;
  return Math.max(0, Math.ceil((target - now) / 1000));
}

/** «3 min 20 s», «45 s». */
export function formatLeft(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${minutes} min ${rest} s` : `${rest} s`;
}
